import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '@/ui/store/gameStore';
import { BIOMES_BY_ID } from '@/config/biomes.config';
import { getActiveEvents } from '@/config/events.config';

/**
 * BiomeIntroBanner — nazwa lokalizacji, która MIGNIE i znika przy wejściu.
 *
 * Zastępuje stałą plakietkę biomu w HUD (usunięta, by nie zajmować miejsca).
 * Pokazuje wyśrodkowany napis (np. „Łąka" / „Plac handlowy") na ~2 s przy:
 *   - zmianie biomu (teleport przez mapę),
 *   - wejściu na plac handlowy,
 *   - powrocie z placu do świata.
 *
 * **Timing — dlaczego `isTeleporting`, a nie `biome:changed`:**
 * Wszystkie 3 ścieżki idą przez `startTeleport()` → `TeleportScene` ustawia
 * `isTeleporting=true` na czas portalu i zeruje je w SHUTDOWN, dokładnie gdy
 * nowy świat się ODSŁANIA (po fazie REVEAL). Łapiemy więc przejście
 * `true → false` — napis pojawia się w momencie, gdy gracz widzi już nowy świat,
 * a nie podczas białego rozbłysku portalu. `currentBiomeId` jest ustawiany PRZED
 * teleportem (BiomeMapModal), więc przy odsłonięciu mamy już poprawną nazwę.
 *
 * **Musi być montowany POZA blokiem `!hideGameUi`** w App.tsx — ten blok znika
 * na czas teleportu (`hideGameUi = isTeleporting || ...`), więc komponent w jego
 * środku remontowałby się po teleporcie i nie wykryłby przejścia `true → false`.
 *
 * Czysto wizualny, `pointer-events-none` — nie blokuje kliknięć w grę.
 */

const FLASH_MS = 2200;

export function BiomeIntroBanner(): JSX.Element | null {
  const isTeleporting = useGameStore((s) => s.isTeleporting);
  const currentBiomeId = useGameStore((s) => s.currentBiomeId);
  const activeScene = useGameStore((s) => s.activeScene);

  const prevTeleporting = useRef(isTeleporting);
  const hideTimer = useRef<number | null>(null);
  // `key` retriggeruje animację CSS przy każdym nowym wejściu (remount napisu).
  // `subtitle` (EV-4): świąteczny podtytuł dla biomu eventu (np. „Wydarzenie: Wakacje Capibar").
  const [banner, setBanner] = useState<{ label: string; subtitle?: string; key: number } | null>(null);

  useEffect(() => {
    const was = prevTeleporting.current;
    prevTeleporting.current = isTeleporting;

    // Tylko przejście true → false = „świat się odsłonił".
    if (!(was && !isTeleporting)) return;

    const label =
      activeScene === 'plaza'
        ? 'Plac handlowy'
        : (BIOMES_BY_ID[currentBiomeId]?.name.pl ?? '');
    if (!label) return;

    // EV-4: wejście na biom AKTYWNEGO eventu → świąteczny podtytuł z nazwą eventu.
    const eventForBiome =
      activeScene === 'plaza'
        ? undefined
        : getActiveEvents().find((ev) => ev.biomeId === currentBiomeId);
    const subtitle = eventForBiome ? `Wydarzenie: ${eventForBiome.name.pl}` : undefined;

    setBanner((b) => ({ label, subtitle, key: (b?.key ?? 0) + 1 }));

    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setBanner(null), FLASH_MS);
  }, [isTeleporting, currentBiomeId, activeScene]);

  // Sprzątanie timera przy unmount (gdyby App się przemontował).
  useEffect(() => {
    return () => {
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    };
  }, []);

  if (banner === null) return null;

  return (
    <div
      className="absolute inset-x-0 top-[18%] z-30 flex justify-center pointer-events-none"
      aria-hidden="true"
    >
      <div
        key={banner.key}
        className={`rounded-2xl px-6 py-2.5 shadow-lg text-center ${
          banner.subtitle ? 'bg-cyan-50/90' : 'bg-white/85'
        }`}
        style={{ animation: `biomeIntroFlash ${FLASH_MS}ms ease-out forwards` }}
      >
        {banner.subtitle && (
          <div className="text-xs sm:text-sm font-bold text-cyan-600 tracking-wide uppercase">
            {banner.subtitle}
          </div>
        )}
        <span
          className={`text-xl sm:text-2xl font-bold tracking-wide ${
            banner.subtitle ? 'text-cyan-700' : 'text-meadow-700'
          }`}
        >
          {banner.label}
        </span>
      </div>

      {/* Keyframes inline (jak ActionMenu) — fade+scale wejście, hold, fade+drift wyjście. */}
      <style>{`
        @keyframes biomeIntroFlash {
          0%   { opacity: 0; transform: translateY(14px) scale(0.92); }
          18%  { opacity: 1; transform: translateY(0) scale(1); }
          78%  { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-12px) scale(0.98); }
        }
      `}</style>
    </div>
  );
}
